/*
 * @format
 */

import JsonApi from 'devour-client';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

import config from '../../env';

export const ACCOUNT = 'account';
export const DEVICE = 'device';
export const DEVICE_FROM_INSTALLER = 'device-from-installer';
export const HOME = 'home';
export const PUSH_TOKEN = 'push-token';
export const RESOLVED_FAULT = 'resolved-fault';
export const DATA_EXPORT = 'dataexport';
export const GEOLOCATION_DEVICE = 'geolocation-device';
export const GEOLOCATION_ACCESS_TOKEN = 'geolocation-access-token';
export const DEREGISTER_SMART_INTERFACE = 'deregister-smart-interface';
export const DEVICE_USER = 'device-user';
export const ACCEPT_USER_INVITE = 'accept-user-invite';
export const RESEND_USER_INVITE = 'resend-user-invite';
export const DSR = 'dsr-feature';
export const DSR_AUTO_ACCEPT = 'auto-accept';
export const REGISTRATION_DIAGNOSTICS = 'registration-diagnostic';
export const PENDING_DSR_COMMAND = 'pending-dsr-command';
export const POSTCODE = 'postcode';
export const HALO_NOTIFICATION_MESSAGE = 'halo-notification-message';

// Default geolocation away setpoint, an integer in 0.1C units (500 = 5.0C).
// This is frost protection - the lowest allowed away temperature - matching the
// API (away temperature can be set as low as frost protection). It doubles as
// the absolute lower bound on the AwayTemperature screen; the per-zone minimum
// then raises it to each zone's frost protection / lower limit lock. Single
// source of truth shared by the API models and the AwayTemperature screen so
// the default can't drift between them.
export const DEFAULT_AWAY_SETPOINT = 500;
export const HALO_NOTIFICATION_MESSAGE_UNREAD_COUNT = 'halo-notification-message-unread-count';

export default () => {
  const apiUrl = `${config.halo_api_endpoint}/halo`;
  const jsonApi = new JsonApi({ apiUrl });

  const cognitoMiddleware = {
    name: 'get-latest-token',
    req: async payload => {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      payload.req.headers.Authorization = token;
      return payload;
    },
  };

  jsonApi.define('dataexport');

  jsonApi.define(DEVICE, {
    SerialNumber: '',
    AddressLine1: '',
    AddressLine2: '',
    AddressCity: '',
    AddressCounty: '',
    AddressCountry: '',
    AddressPostcode: '',
    PublicKey: '',
    FriendlyName: '',
    PropertyType: '',
    ReRegister: false,

    PrintedSerialNumber: '',
    CloudLicense: '',

    Latitude: '',
    Longitude: '',
    HomeRadius: '',
    AwayRadius: '',
    GeolocationEnabled: '',
    GeolocationAwaySetpointEnabled: false,
    AwaySetpoint: DEFAULT_AWAY_SETPOINT,

    RegisteredBy: '',
    RegisteredById: '',
    RegisteredAt: 0,
    WeatherCompensationEnabled: '',

    UpdatedBy: '',
    UpdatedById: '',
    UpdatedAt: 0,

    Status: '',
    AlarmCount: 0,
    Type: '',
  });

  jsonApi.define(DEVICE_FROM_INSTALLER, {
    Code: '',
    CloudLicense: false,
    DeviceType: '',

    Device: {
      jsonApi: 'hasOne',
      type: 'devices',
    },
  });

  jsonApi.define(DEREGISTER_SMART_INTERFACE, {
    SerialNumber: '',
  });

  jsonApi.define('device-check', {
    SerialNumber: '',
    PublicKey: '',
    ConnectionValidAfter: 0,
    Online: false,
    PrintedSerialNumber: '',
    FirmwareUpdateState: '',
  });

  jsonApi.define(PUSH_TOKEN, {
    CognitoId: '',
    DeviceToken: '',
    AppSource: '',
    Os: '',
  });

  jsonApi.define('PendingInvite', {
    Id: '',
    Email: '',
    FirstName: '',
    LastName: '',
    Role: '',
    DeviceRegistrationId: '',
    CreatedByName: '',
  });

  jsonApi.define('property', {
    AddressLine1: '',
    AddressCity: '',
    AddressCountry: '',
    AddressCounty: '',
    AddressPostcode: '',
    Priority: false,
    PropertyType: '',
    PropertySize: '',
    Notes: '',
  });

  jsonApi.define(ACCOUNT, {
    Email: '',
    FirstName: '',
    LastName: '',
    AcceptedMarketing: false,
    TermsAgreedAt: '',
    TermsVersion: 1,

    PendingInvites: {
      jsonApi: 'hasMany',
      type: 'PendingInvite',
    },
  });

  jsonApi.define(RESOLVED_FAULT, {
    FaultCode: '',
    Description: '',
    Severity: '',
    RaisedAt: 1,
    ResolvedAt: 1,
  });

  jsonApi.define(HOME, {
    deviceRegistrationId: '',
    serialNumber: '',
    friendlyName: '',
    connectedDevice: '',
    latitude: '',
    longitude: '',
    zones: [],
    zigbeeRepeaters: [],
    boiler: {},
    controlFaults: [],
    swc: {},
    si: {},
    hpi: {},
    logic: {},
    // Per-interface DESIRED control state projected onto each aws-target home.
    // Declared so devour keeps it: for a plain attribute devour only gates on
    // the key being present in the model and then copies the response value
    // verbatim, so this {} placeholder preserves the whole nested
    // { interfaceId, family, stateVersion, goals, resolved } object intact
    // (it is never merged or reshaped). Absent on cf/legacy homes, where it
    // stays undefined.
    desiredState: {},
    role: '',
    haloDeviceServiceEnabled: false,
    // The device's reconciler routing target ('aws' | 'cf' | 'legacy'). Declared
    // so devour keeps it: it is the DEFINITIVE signal for whether a home is still
    // aws-target, used to prune cached desired state safely (a missing block
    // alone is ambiguous — it also occurs on a transient read fault). Absent on
    // older backends that do not serialise it yet, where it stays undefined.
    haloDeviceServiceTarget: '',
    geolocationDevices: [],
    geolocationEnabled: false,
    awaySetpointEnabled: false,
    awaySetpoint: DEFAULT_AWAY_SETPOINT,
    geolocationAwayTemperatureAvailable: false,
    dsrAutoAccept: false,
    homeRadius: 250,
    awayRadius: 500,
    weatherCompensationEnabled: false,
    weatherCompensationUserEnabled: false,

    upgradePurchasePossible: false,
    googleHomeEnabled: false,
    awaitingZones: false,
    property: {},

    type: '',
  });

  jsonApi.define(GEOLOCATION_DEVICE, {
    GeolocationDeviceId: '',
    DeviceRegistrationId: '',
    FriendlyName: '',
    Sensitivity: 0,
    LastSeenAt: 0,
    CreatedAt: 0,
  });

  jsonApi.define(GEOLOCATION_ACCESS_TOKEN, {
    token: '',
    geolocationDeviceId: '',
    cognitoId: '',
    updatedAt: 0,
    Token: '',
    GeolocationDeviceId: '',
    CognitoId: '',
    UpdatedAt: 0,
  });

  jsonApi.define(DEVICE_USER, {
    email: '',
    role: '',

    firstName: '',
    lastName: '',

    createdBy: '',
    createdByName: '',
    createdAt: 0,
    status: '',

    device: {
      jsonApi: 'hasOne',
      type: 'devices',
    },
  });

  jsonApi.define(ACCEPT_USER_INVITE);
  jsonApi.define(RESEND_USER_INVITE);

  jsonApi.define(REGISTRATION_DIAGNOSTICS, {
    Timestamp: 0,
    Index: 0,
    Url: '',
  });

  jsonApi.define(
    DSR_AUTO_ACCEPT,
    {
      id: '',
      autoAccept: false,
    },
    { collectionPath: DSR_AUTO_ACCEPT },
  );

  jsonApi.define(PENDING_DSR_COMMAND, {
    deviceRegistrationId: '',
    id: '',
    mode: '',
    overrideDuration: 0,
    requestId: '',
    setpoint: 0,
    ttl: 0,
    zoneId: '',
    status: '',
  });

  jsonApi.define(POSTCODE, {
    Postcode: '',
    Town: '',
    Thoroughfare: '',
    BuildingNumber: '',
    BuildingName: '',
    SubBuildingName: '',
    OrganisationName: '',
    Locality: '',
    DependentThoroughfare: '',
    DoubleDependentLocality: '',
    POBox: '',
    DepartmentName: '',
  });

  jsonApi.define(HALO_NOTIFICATION_MESSAGE, {
    Title: '',
    BodyHtml: '',
    TargetAudience: '',
    IsRead: false,
    ReadAt: '',
    DeliveredAt: '',
    CreatedBy: '',
    CreatedByName: '',
  });

  jsonApi.define(
    HALO_NOTIFICATION_MESSAGE_UNREAD_COUNT,
    {
      count: 0,
    },
    { collectionPath: 'halo-notification-message-unread-count' },
  );

  const requestLogging = {
    name: 'request-logging',
    req: payload => {
      const model = payload?.req?.model || 'Unknown';
      console.log(new Date().toJSON(), `"${model}" model API Request`, payload);
      return payload;
    },
  };

  const responseLogging = {
    name: 'response-logging',
    res: payload => {
      const model = payload?.req?.model || 'Unknown';
      if (
        payload &&
        payload.res &&
        payload.res.status >= 200 &&
        payload.res.status < 300
      ) {
        console.log(
          new Date().toISOString(),
          `"${model}" JsonApi API Response `,
          payload,
        );
      } else {
        console.log(
          new Date().toISOString(),
          `"${model}" JsonApi API Error Response`,
          payload,
        );
      }

      return payload;
    },
  };

  const cancellableRequest = {
    name: 'axios-cancellable-request',
    req(payload) {
      const { jsonApi: localJsonApi } = payload;

      if (!payload.req.params || !payload.req.params.opts) {
        return localJsonApi.axios(payload.req);
      }

      const { opts } = payload.req.params;
      const signal = opts?.signal;
      const timeout = opts?.timeout;
      const extraHeaders = opts?.headers;

      if (opts) {
        delete payload.req.params.opts;
      }

      if (extraHeaders) {
        Object.assign(payload.req.headers, extraHeaders);
      }

      return localJsonApi.axios(
        Object.assign(payload.req, {
          signal,
          timeout,
        }),
      );
    },
  };

  const errorMiddleware = {
    name: 'is-it-a-cancel',
    error(payload) {
      if (axios.isCancel(payload)) {
        console.log('Request canceled', payload);

        return {
          cancelled: true,
        };
      } else {
        return payload;
      }
    },
  };

  jsonApi.replaceMiddleware('axios-request', cancellableRequest);
  jsonApi.insertMiddlewareBefore('axios-cancellable-request', requestLogging);
  jsonApi.insertMiddlewareBefore(
    'axios-cancellable-request',
    cognitoMiddleware,
  );
  jsonApi.insertMiddlewareBefore('response', responseLogging);
  jsonApi.insertMiddlewareAfter('errors', errorMiddleware);

  return jsonApi;
};
